/**
 * AdminCopyAttempts — /admin/content-protection
 *
 * Displays aggregated copy / cut / right-click / print / save attempts
 * across the last N hours grouped by user. Users hitting the hourly
 * threshold are flagged red so moderators can act.
 *
 * Backend: GET  /api/admin/content-protection/copy-attempts?hours=N
 *          GET  /api/admin/content-protection/copy-attempts/{user_id}?hours=N
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ShieldAlert, Copy, MousePointerClick, Printer, Save, Ban, RefreshCw, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const KIND_META = {
  copy:            { icon: Copy,              label: 'Copy' },
  cut:             { icon: Copy,              label: 'Cut' },
  right_click:     { icon: MousePointerClick, label: 'Right-click' },
  print:           { icon: Printer,           label: 'Print' },
  save:            { icon: Save,              label: 'Save' },
  drag:            { icon: MousePointerClick, label: 'Drag' },
  screenshot_key:  { icon: Copy,              label: 'Screenshot key' },
};

const HOUR_PRESETS = [1, 6, 24, 72, 168];

const AdminCopyAttempts = () => {
  const { API } = useContext(AuthContext);
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailUser, setDetailUser] = useState(null);
  const [detailRows, setDetailRows] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/content-protection/copy-attempts`, {
        params: { hours },
      });
      setData(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load copy attempts.');
    } finally {
      setLoading(false);
    }
  }, [API, hours]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (userId) => {
    setDetailUser(userId);
    setDetailRows(null);
    try {
      const { data: res } = await axios.get(
        `${API}/admin/content-protection/copy-attempts/${userId}`,
        { params: { hours } },
      );
      setDetailRows(res.attempts || []);
    } catch (err) {
      toast.error('Failed to load detail.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="admin-copy-attempts-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-primary" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Content protection</div>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight mt-1">
              Copy-attempt monitor
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Users triggering more than <strong>{data?.hourly_threshold ?? 20}</strong> copy /
              right-click / print / save attempts in any 60-minute window are flagged.
            </p>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Range picker */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="hour-range-picker">
          <span className="text-xs text-muted-foreground mr-1">Window:</span>
          {HOUR_PRESETS.map((h) => (
            <button
              key={h}
              onClick={() => setHours(h)}
              className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                hours === h
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted/40'
              }`}
              data-testid={`hours-${h}-btn`}
            >
              {h < 24 ? `${h}h` : `${h / 24}d`}
            </button>
          ))}
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Total attempts</div>
            <div className="text-2xl font-heading font-black text-foreground" data-testid="kpi-total">{data?.total_attempts ?? '—'}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Users active</div>
            <div className="text-2xl font-heading font-black text-foreground">{data?.offenders?.length ?? 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Flagged users</div>
            <div className="text-2xl font-heading font-black text-red-600" data-testid="kpi-flagged">{data?.flagged_users ?? 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Hourly threshold</div>
            <div className="text-2xl font-heading font-black text-foreground">{data?.hourly_threshold ?? 20}</div>
          </CardContent></Card>
        </div>

        {/* Offenders table */}
        <Card>
          <CardHeader>
            <CardTitle>Offenders</CardTitle>
            <CardDescription>Sorted by peak hourly rate. Click a row for the full attempt log.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading && <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>}
            {!loading && (!data?.offenders?.length) && (
              <div className="p-8 text-center text-muted-foreground text-sm" data-testid="offenders-empty">
                No copy-attempt telemetry in this window. That&rsquo;s a good thing.
              </div>
            )}
            {!loading && data?.offenders?.length > 0 && (
              <div className="divide-y divide-border">
                {data.offenders.map((row) => (
                  <button
                    key={row.user_id}
                    onClick={() => openDetail(row.user_id)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/40 transition-colors"
                    data-testid={`offender-row-${row.user_id}`}
                  >
                    {row.flagged ? (
                      <Badge variant="destructive" className="text-[10px] uppercase tracking-widest" data-testid="flagged-badge">Flagged</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px] uppercase tracking-widest">Watch</Badge>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-heading font-extrabold text-foreground truncate">{row.email}</div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                        <span><strong>{row.total}</strong> total</span>
                        <span>·</span>
                        <span><strong>{row.peak_hour}</strong> peak/hour</span>
                        <span>·</span>
                        <span>last {new Date(row.last_at).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        {Object.entries(row.kinds || {}).map(([k, n]) => {
                          const meta = KIND_META[k] || { icon: Copy, label: k };
                          const Icon = meta.icon;
                          return (
                            <span
                              key={k}
                              className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-muted"
                            >
                              <Icon className="w-2.5 h-2.5" /> {meta.label} × {n}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Link
                        to={`/admin/users/${row.user_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1"
                        data-testid={`view-user-${row.user_id}`}
                      >
                        <Ban className="w-3 h-3" /> Manage
                      </Link>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Drill-down modal */}
        {detailUser && (
          <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => { setDetailUser(null); setDetailRows(null); }}
            data-testid="attempt-detail-modal"
          >
            <div
              className="bg-background rounded-2xl border border-border max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-border">
                <h3 className="font-heading text-xl font-black">Attempt log</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Last {hours < 24 ? `${hours}h` : `${hours / 24}d`} · {detailRows?.length ?? '…'} rows
                </p>
              </div>
              <div className="p-4">
                {!detailRows && <div className="text-sm text-muted-foreground">Loading…</div>}
                {detailRows?.length === 0 && <div className="text-sm text-muted-foreground">No attempts.</div>}
                {detailRows?.length > 0 && (
                  <ul className="space-y-1.5 text-xs font-mono">
                    {detailRows.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 py-1 border-b border-border/60">
                        <Badge variant="outline" className="text-[10px]">{r.kind}</Badge>
                        <span className="text-muted-foreground shrink-0">{new Date(r.created_at).toLocaleString()}</span>
                        <span className="truncate">{r.lesson_id || '—'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCopyAttempts;
